// TanStack-Table-backed entity table. Displays a column per discovered prop.
// Falls back gracefully when given non-Entity rows.

import { flexRender, getCoreRowModel, useReactTable, type ColumnDef } from "@tanstack/react-table";
import type { WidgetComponent } from "@viz/presentation";
import type { Entity } from "@viz/types";
import { useMemo } from "react";

interface Row { id: string; type: string; [k: string]: unknown }

const EntityTable: WidgetComponent = ({ props, onDrill }) => {
  const entities = (props.entities as Entity[] | undefined) ?? [];
  const rows: Row[] = useMemo(() => entities.map(e => {
    const r: Row = { id: e.id, type: e.type };
    for (const [k, v] of Object.entries(e.props)) {
      // Props arrive wrapped as { type, value }. Unwrap explicitly — the
      // earlier `?? v` fallback dumped the wrapper object as JSON when the
      // inner value was null / undefined.
      const obj = v as unknown as { value?: unknown } | null;
      if (obj && typeof obj === "object" && "value" in obj) {
        const inner = obj.value;
        r[k] = inner == null ? "" : inner;
      } else {
        r[k] = v == null ? "" : (v as unknown);
      }
    }
    return r;
  }), [entities]);

  const columns: ColumnDef<Row>[] = useMemo(() => {
    const cols: ColumnDef<Row>[] = [
      { accessorKey: "id", header: "ID", cell: ctx => (
        <button
          className="font-mono text-xs text-sky-700 hover:underline"
          onClick={() => onDrill?.(ctx.row.original.id)}
        >{ctx.row.original.id}</button>
      ) },
    ];
    const seen = new Set(["id", "type"]);
    // Only include the type column if there's actually variation in it —
    // otherwise it eats space showing the same value on every row.
    const distinctTypes = new Set(rows.map(r => String(r.type ?? "")));
    if (distinctTypes.size > 1) {
      cols.push({ accessorKey: "type", header: "Type" });
    }
    for (const r of rows) {
      for (const k of Object.keys(r)) {
        if (seen.has(k)) continue;
        seen.add(k);
        cols.push({ accessorKey: k, header: k, cell: ctx => formatCell(ctx.getValue()) });
      }
    }
    return cols;
  }, [rows, onDrill]);

  const table = useReactTable({ data: rows, columns, getCoreRowModel: getCoreRowModel() });

  return (
    <div className="overflow-auto">
      <table className="min-w-full border-collapse text-sm">
        <thead className="sticky top-0 bg-slate-50">
          {table.getHeaderGroups().map(hg => (
            <tr key={hg.id}>
              {hg.headers.map(h => (
                <th key={h.id} className="border-b border-slate-200 px-3 py-2 text-left text-xs uppercase tracking-wider text-slate-500">
                  {flexRender(h.column.columnDef.header, h.getContext())}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map(r => (
            <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50">
              {r.getVisibleCells().map(c => (
                <td key={c.id} className="px-3 py-1.5 align-top text-slate-800">
                  {flexRender(c.column.columnDef.cell, c.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length === 0 && <div className="p-6 text-center text-sm text-slate-500">No entities.</div>}
    </div>
  );
};

function formatCell(v: unknown): string {
  if (v == null) return "—";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

export default EntityTable;
